import * as ApiUtils from 'utils/api-utils';

import type {List, Map, MapOf} from 'immutable';

type ImmutableMap = Map<string, unknown>;

export const fetchSettings = () => {
    return ApiUtils.callApiMeteorAuthenticatedUnsafe({
        endpoint: `/settings`,
        method: 'GET',
    });
};

export const updateSettings = (payload) => {
    return ApiUtils.callApiMeteorAuthenticatedUnsafe({
        endpoint: `/settings`,
        method: 'PUT',
        data: payload,
    });
};

export const fetchNotificationOptOuts = (client, user) => {
    return ApiUtils.callApiMeteor(client, user, {
        endpoint: `/settings/notifications`,
        method: 'GET',
    });
};

export const fetchGlobalNotificationOptOuts = (client, user) => {
    return ApiUtils.callApiMeteor(client, user, {
        endpoint: `/settings/notifications/global`,
        method: 'GET',
    });
};

export const fetchHonorCodes = () => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: '/settings/honor-codes',
        method: 'GET',
    });
};

/**
 * Get the default honor code for a client
 */
export const fetchDefaultHonorCode = () => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: `/settings/honor-code`,
        method: 'GET',
    });
};

interface HonorCode {
    honorCodeId: string;
    text: string;
    name: string;
    default: boolean;
}

export const updateHonorCode = (honorCode: HonorCode) => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: `/settings/honor-code`,
        method: 'POST',
        data: honorCode,
    });
};

export const deleteHonorCode = (honorCodeId: string) => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: `/settings/honor-code/${honorCodeId}`,
        method: 'DELETE',
    });
};

export const fetchServicesUnsafe = () => {
    return ApiUtils.callApiMeteorAuthenticatedUnsafe({
        endpoint: '/settings/services',
        method: 'GET',
    });
};

/**
 * Enable or disable editable app features
 * @param appFeatureSettings
 */
export const updateAppFeatures = (appFeatureSettings): Promise<null> => {
    return ApiUtils.callApiMeteorAuthenticated({
        method: 'post',
        endpoint: '/settings/app-features',
        data: appFeatureSettings,
    });
};

export type IpVideoFeed = MapOf<{
    port: unknown | null;
    inUse: boolean;
    sourceNumber: unknown | null;
    clientId: string;
    createdTs: string;
    ipAudioFeedId: string | null;
    name: string;
    ipVideoFeedId: string;
    ipAddress: string;
    ipAudioFeed: unknown | null;
    rtspUrl: string | null;
    username: string;
    updatedTs: string;
    ipFeedTypeId: string;
    password: string;
    requiresSsl: boolean;
    sort?: number;
}>;

export type IpVideoFeedsResponse = List<IpVideoFeed>;

/**
 * Get all IP video feeds for a client.
 */
export const fetchIpVideoFeeds = () => {
    return ApiUtils.callApiMeteorAuthenticated<IpVideoFeedsResponse>({
        endpoint: '/settings/ip-feeds/video/',
        method: 'GET',
    });
};

export const fetchIpVideoFeed = (ipVideoFeedId: string): Promise<ImmutableMap> => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: `/settings/ip-feeds/video/${ipVideoFeedId}`,
        method: 'GET',
    });
};

export const getIpVideoFeedsImportTemplate = (): Promise<string> => {
    return ApiUtils.callApiMeteorAuthenticatedJS<string>({
        endpoint: '/settings/ip-feeds/video/import-template',
        method: 'get',
    });
};

export const bulkImportIpVideoFeeds = (
    feeds: Record<string, unknown>[],
): Promise<{created: {ipVideoFeedId: string; name: string}[]; failed: {name: string; error: string}[]}> => {
    return ApiUtils.callApiMeteorAuthenticatedJS({
        endpoint: '/settings/ip-feeds/video/bulk-import',
        method: 'post',
        data: {feeds},
    });
};

export const updateIpVideoFeed = (payload: ImmutableMap): Promise<ImmutableMap> => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: '/settings/ip-feeds/video/',
        method: 'PUT',
        data: payload,
    });
};

export const createIpVideoFeed = (payload: ImmutableMap): Promise<ImmutableMap> => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: '/settings/ip-feeds/video/',
        method: 'POST',
        data: payload,
    });
};

/**
 * Delete an IP video feed by id.
 */
export const deleteIpVideoFeed = (ipVideoFeedId: string): Promise<ImmutableMap> => {
    return ApiUtils.callApiMeteorAuthenticated({
        method: 'DELETE',
        endpoint: `/settings/ip-feeds/video/${ipVideoFeedId}`,
    });
};

/**
 * Get all IP audio feeds for a client.
 */
export const fetchIpAudioFeeds = (): Promise<List<ImmutableMap>> => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: '/settings/ip-feeds/audio/',
        method: 'GET',
    });
};

export const fetchIpAudioFeed = (ipAudioFeedId: string): Promise<ImmutableMap> => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: `/settings/ip-feeds/audio/${ipAudioFeedId}`,
        method: 'GET',
    });
};

export const updateIpAudioFeed = (payload: ImmutableMap): Promise<ImmutableMap> => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: '/settings/ip-feeds/audio/',
        method: 'PUT',
        data: payload,
    });
};

export const createIpAudioFeed = (payload: ImmutableMap): Promise<ImmutableMap> => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: '/settings/ip-feeds/audio/',
        method: 'POST',
        data: payload,
    });
};

/**
 * Delete an IP audio feed by id.
 */
export const deleteIpAudioFeed = (ipAudioFeedId: string): Promise<ImmutableMap> => {
    return ApiUtils.callApiMeteorAuthenticated({
        method: 'DELETE',
        endpoint: `/settings/ip-feeds/audio/${ipAudioFeedId}`,
    });
};

/**
 * Fetches the environment settings. These settings are not specific to a client.
 */
export const fetchEnvironment = () => {
    return ApiUtils.callApiMeteorUnauthenticatedUnsafe({
        endpoint: `/settings/environment`,
        method: 'GET',
    });
};

export const updateLayout = (payload) => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: `/settings/layouts/update`,
        method: 'PUT',
        data: payload,
    });
};

export const exportLayout = (payload) => {
    return ApiUtils.callApiMeteorAuthenticated({
        endpoint: `/settings/layouts/export`,
        method: 'PUT',
        data: payload,
    });
};

export type CreateBlankLayoutResponse = MapOf<{
    name: string;
    layoutId?: string;
    dirty?: boolean;
    roomLayouts?: List<
        MapOf<{
            roomId: string;
            x: number;
            y: number;
            width: number;
            height: number;
            cachedWidth: number;
            cachedHeight: number;
            doorPositionId: string;
        }>
    >;
}>;

export const createBlankLayout = (payload: ImmutableMap) => {
    return ApiUtils.callApiMeteorAuthenticated<CreateBlankLayoutResponse>({
        endpoint: `/settings/layouts`,
        method: 'POST',
        data: payload,
    });
};
